import { useEffect, useCallback, useState } from "react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";

/**
 * useOnboardingTour — Hook para tour de bienvenida del usuario nuevo.
 *
 * Iter25 — Solo primera vez después del login. Re-activable desde el botón "?"
 * en topbar (despachando `localStorage.removeItem('onboarding_completed_v1')`).
 *
 * Uso:
 *   const { startTour, isComplete } = useOnboardingTour();
 *   useEffect(() => { if (shouldShow) startTour(); }, [shouldShow]);
 *
 * El tour respeta tema brand-burgundy.
 */
const STORAGE_KEY = "onboarding_completed_v1";

export function useOnboardingTour() {
  const [isComplete, setIsComplete] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return true;
    }
  });

  const markComplete = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch { /* noop */ }
    setIsComplete(true);
  }, []);

  const startTour = useCallback(() => {
    const d = driver({
      showProgress: true,
      animate: true,
      smoothScroll: true,
      allowClose: true,
      overlayColor: "rgba(35, 28, 39, 0.65)",
      progressText: "Paso {{current}} de {{total}}",
      nextBtnText: "Siguiente →",
      prevBtnText: "← Anterior",
      doneBtnText: "Empezar",
      onDestroyStarted: () => {
        markComplete();
        d.destroy();
      },
      steps: [
        {
          element: '[data-tour="dashboard-new-case"]',
          popover: {
            title: "👋 Bienvenido a JustSapiens Procure",
            description:
              "Aquí gestionas tu proceso de contratación pública en 3 pasos: Prevalidación gratuita → Diagnóstico → Kit Documental. <br/><br/>Para empezar, haz click en <strong>'Nuevo caso'</strong>.",
            side: "bottom",
            align: "start",
          },
        },
        {
          element: '[data-tour="case-flow-stepper"], [data-testid="case-flow-stepper"]',
          popover: {
            title: "🧭 Tu mapa de proceso",
            description:
              "Cada caso pasa por 3 fases:<br/>" +
              "<strong>1. Prevalidación</strong> (gratis, 1-3 min)<br/>" +
              "<strong>2. Diagnóstico</strong> ($350K o 1 cuota gratis, 24-48h con revisión humana)<br/>" +
              "<strong>3. Kit Documental</strong> ($1.2M o 1 cuota gratis, 48-72h con revisión humana)<br/><br/>" +
              "Este stepper te muestra siempre en qué fase estás y qué sigue.",
            side: "bottom",
            align: "center",
          },
        },
        {
          element: '[data-tour="case-status-badge"], [data-tour="case-flow-stepper"]',
          popover: {
            title: "🏷️ Estado del caso",
            description:
              "En la parte superior siempre verás el estado actual y el <strong>próximo paso</strong> con un botón directo.<br/><br/>" +
              "Si tienes <strong>cuotas gratuitas</strong>, verás un botón verde <em>'Usar cuota gratuita'</em> que evita la pasarela de pago.",
            side: "bottom",
            align: "center",
          },
        },
        {
          element: '[data-tour="flow-guide-trigger"], [data-testid^="flow-guide-"]',
          popover: {
            title: "❓ ¿Dudas? Botón 'Ver guía'",
            description:
              "En cada paso encuentras una <strong>guía expandible</strong> que explica:<br/>" +
              "• Qué es ese paso<br/>" +
              "• Qué te entrega exactamente<br/>" +
              "• Cómo funciona la IA detrás (dónde investiga, qué encuentra)<br/>" +
              "• Cómo pasar al siguiente nivel<br/><br/>" +
              "Siempre que tengas duda, click en <strong>'Ver guía'</strong> o en el botón <em>?</em> arriba a la derecha.",
            side: "left",
            align: "center",
          },
        },
      ],
    });
    d.drive();
  }, [markComplete]);

  const resetTour = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch { /* noop */ }
    setIsComplete(false);
  }, []);

  // Auto-start: cuando montas el hook y el tour no ha sido completado, espera 1.2s y arranca.
  useEffect(() => {
    if (isComplete) return;
    const timer = setTimeout(() => {
      // Solo si el elemento root del primer paso existe (estamos en dashboard)
      const anchor = document.querySelector('[data-tour="dashboard-new-case"]');
      if (anchor) startTour();
    }, 1200);
    return () => clearTimeout(timer);
  }, [isComplete, startTour]);

  return { startTour, resetTour, isComplete, markComplete };
}

export default useOnboardingTour;
